import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import { useAuthStore } from '@/store/authStore';
import { reportAppError } from '@/shared/lib/appErrorStore.ts';

const api: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json, text/plain, */*',
    'app-client-key': env.appClientKey,
    appversion: env.appVersion,
    devicetype: env.deviceType,
  },
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token ?? localStorage.getItem('auth_token');
  if (token) {
    // MyU services authenticate via this custom header only — sending
    // `Authorization: Bearer …` causes the CORS preflight to fail because
    // it is not in the server's `Access-Control-Allow-Headers` list.
    config.headers['myu-auth-token'] = token;
  }

  // Multipart guard: when the body is `FormData`, the browser must set
  // the `Content-Type` header so the boundary string is included. Our
  // global default of `application/json` would otherwise stick and the
  // server would parse the body with the wrong content type (often
  // surfacing as a generic 500). Strip the header so axios/the browser
  // can do the right thing.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers) {
      // Remove every casing variant — axios normalizes, but defensive.
      delete (config.headers as Record<string, unknown>)['Content-Type'];
      delete (config.headers as Record<string, unknown>)['content-type'];
      if (typeof (config.headers as { delete?: (k: string) => void }).delete === 'function') {
        (config.headers as { delete: (k: string) => void }).delete('Content-Type');
      }
    }
  }

  return config;
});

/* -------------------------------------------------------------------------- */
/* Transient-error retry                                                      */
/* -------------------------------------------------------------------------- */
/**
 * Retry idempotent (GET/HEAD/OPTIONS) requests once or twice when the network
 * misbehaves transiently. Covers:
 *   • DNS hiccups (`EAI_AGAIN`, `ENOTFOUND`)
 *   • Socket resets / timeouts
 *   • The 502 envelope our Vite dev proxy synthesizes for transient upstream
 *     errors (`{ statusCode: '502', transient: true }`).
 *
 * Mutations (POST/PUT/DELETE/PATCH) are NOT retried because a half-committed
 * request could create duplicates.
 */
const MAX_RETRIES = 2;
const TRANSIENT_NETWORK_CODES = new Set([
  'EAI_AGAIN',
  'ENOTFOUND',
  'ETIMEDOUT',
  'ECONNRESET',
  'ECONNREFUSED',
  'EPIPE',
  'ECONNABORTED', // axios timeout
]);

function isTransientFailure(error: AxiosError): boolean {
  // No response at all → almost always a network/DNS problem.
  if (!error.response) {
    const code = (error.code ?? '').toUpperCase();
    return TRANSIENT_NETWORK_CODES.has(code) || error.message === 'Network Error';
  }
  // Vite dev proxy 502 with `transient: true` envelope.
  const data = error.response.data as { transient?: boolean } | undefined;
  if (error.response.status === 502 && data?.transient === true) return true;
  return false;
}

function isRetryableRequest(config: AxiosRequestConfig | undefined): boolean {
  if (!config) return false;
  const method = (config.method ?? 'get').toLowerCase();
  return method === 'get' || method === 'head' || method === 'options';
}

type RetryableConfig = AxiosRequestConfig & { _retryCount?: number };

async function maybeRetry(error: AxiosError): Promise<unknown> {
  const config = error.config as RetryableConfig | undefined;
  if (!config || !isRetryableRequest(config) || !isTransientFailure(error)) {
    throw error;
  }

  config._retryCount = (config._retryCount ?? 0) + 1;
  if (config._retryCount > MAX_RETRIES) throw error;

  // Exponential-ish backoff: 250ms, 750ms.
  const delay = 250 * Math.pow(3, config._retryCount - 1);
  await new Promise((resolve) => setTimeout(resolve, delay));

  if (import.meta.env.DEV) {
    console.info(
      `[api] retry ${config._retryCount}/${MAX_RETRIES} → ${config.method?.toUpperCase() ?? 'GET'} ${config.url}`,
    );
  }
  return api.request(config);
}

/** Pull a human-readable message out of any MyU/Axios error shape. */
function extractErrorMessage(error: AxiosError): string {
  const data = error.response?.data as
    | {
        errorMessage?: unknown;
        message?: unknown;
        error?: unknown;
        errors?: unknown;
      }
    | string
    | undefined;

  // Normalize any value (string, array of validation objects, nested object)
  // into a single human-readable string.
  const toMessage = (value: unknown): string | null => {
    if (value == null) return null;
    if (typeof value === 'string') return value.trim() || null;
    if (Array.isArray(value)) {
      const parts = value
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object') {
            const obj = item as { message?: unknown; field?: unknown };
            if (typeof obj.message === 'string') {
              return typeof obj.field === 'string' ? `${obj.field}: ${obj.message}` : obj.message;
            }
          }
          return null;
        })
        .filter((part): part is string => Boolean(part));
      return parts.length ? parts.join('; ') : null;
    }
    if (typeof value === 'object') {
      const obj = value as { message?: unknown; field?: unknown };
      if (typeof obj.message === 'string') {
        return typeof obj.field === 'string' ? `${obj.field}: ${obj.message}` : obj.message;
      }
    }
    return null;
  };

  if (typeof data === 'string') {
    const msg = data.trim();
    if (msg) return msg;
  } else if (data && typeof data === 'object') {
    const fromErrorMessage = toMessage(data.errorMessage);
    if (fromErrorMessage) return fromErrorMessage;
    const fromMessage = toMessage(data.message);
    if (fromMessage) return fromMessage;
    const fromErrors = toMessage(data.errors);
    if (fromErrors) return fromErrors;
    const fromError = toMessage(data.error);
    if (fromError) return fromError;
  }
  if (!navigator.onLine) {
    return 'No internet connection detected. Please check your network and try again.';
  }
  if (error.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return 'Unable to connect to the server. Please check your connection.';
  }
  return error.message || 'Something went wrong. Please try again.';
}

/**
 * Read the `meta.suppressGlobalErrorToast` opt-out flag from a request config.
 * Callers that already render their own inline error UI (e.g. login form,
 * change-password modal) can set this to skip the global popup.
 */
function shouldSuppressToast(config: AxiosRequestConfig | undefined, message?: string): boolean {
  const meta = (
    config as
      | (AxiosRequestConfig & {
          meta?: {
            suppressGlobalErrorToast?: boolean;
            suppressGlobalErrorMessages?: readonly string[];
          };
        })
      | undefined
  )?.meta;
  return Boolean(
    meta?.suppressGlobalErrorToast ||
    (message && meta?.suppressGlobalErrorMessages?.includes(message)),
  );
}

/** Optional context-specific heading for errors surfaced by the global dialog. */
function getErrorTitle(config: AxiosRequestConfig | undefined): string | undefined {
  const meta = (config as (AxiosRequestConfig & { meta?: { errorTitle?: string } }) | undefined)
    ?.meta;
  return meta?.errorTitle;
}

/**
 * Read the `meta.suppressLogoutOn401` opt-out flag from a request config.
 * Callers that are probing experimental endpoints (which may legitimately
 * 401 for non-auth reasons like "endpoint doesn't exist on this build")
 * can set this to skip the auto-logout side-effect on 401. The error is
 * still thrown to the caller — only the global `useAuthStore.logout()`
 * call is skipped.
 */
function shouldSuppressLogout(config: AxiosRequestConfig | undefined): boolean {
  const meta = (
    config as (AxiosRequestConfig & { meta?: { suppressLogoutOn401?: boolean } }) | undefined
  )?.meta;
  return Boolean(meta?.suppressLogoutOn401);
}

/**
 * A 401 may come from a public/optional request or from an older request that
 * finishes after a new login. Only the request carrying the currently active
 * token is authoritative enough to invalidate the current session.
 */
function requestUsedCurrentSession(config: AxiosRequestConfig | undefined): boolean {
  const currentToken = useAuthStore.getState().token;
  if (!currentToken || !config?.headers) return false;

  const headers = config.headers as unknown as {
    get?: (name: string) => unknown;
    [key: string]: unknown;
  };
  const configuredToken =
    (typeof headers.get === 'function' ? headers.get('myu-auth-token') : undefined) ??
    headers['myu-auth-token'] ??
    headers['Myu-Auth-Token'];

  return typeof configuredToken === 'string' && configuredToken === currentToken;
}

api.interceptors.response.use(
  (response) => {
    // MyU services sometimes return HTTP 200 with an error envelope:
    //   { statusCode: "4XX", errorMessage: "Username or password incorrect" }
    const body = response.data as
      | { statusCode?: string | number; errorMessage?: string; message?: string }
      | undefined;
    const code = body?.statusCode != null ? String(body.statusCode).toUpperCase() : '';
    const isLogicalError = /^[45](XX|\d{2})$/.test(code);
    if (isLogicalError) {
      const message = body?.errorMessage || body?.message || `Request failed (${code}).`;
      const err = new Error(message) as Error & { statusCode?: string };
      err.statusCode = code;
      if (!shouldSuppressToast(response.config, message)) {
        reportAppError({ message, code, title: getErrorTitle(response.config) });
      }
      return Promise.reject(err);
    }
    return response;
  },
  (error: AxiosError) => {
    // 1. Transparently retry transient failures (DNS, socket reset, dev-proxy
    //    502 with `transient: true`). If a retry succeeds we never reach the
    //    rest of this handler — React Query gets a normal response.
    return maybeRetry(error).catch((finalError: AxiosError) => {
      const status = finalError.response?.status;
      const message = extractErrorMessage(finalError);

      if (
        status === 401 &&
        !shouldSuppressLogout(finalError.config) &&
        requestUsedCurrentSession(finalError.config)
      ) {
        useAuthStore.getState().logout();
      }

      // Replace the unhelpful default axios message with the backend's text so
      // React Query consumers (`mutation.error.message`) show the real reason.
      finalError.message = message;
      // Attach a stringified status code for consumers that want to display it.
      const statusCode = status != null ? String(status) : 'NETWORK';
      (finalError as AxiosError & { statusCode?: string }).statusCode = statusCode;

      // Surface the server's `errorMessage` (or a network-level fallback) as
      // a global popup. Skip when the caller opted out, when the request was
      // canceled, and when the user has just been logged out via 401 (the
      // login screen will already render its own dialog after redirect).
      const canceled = (finalError as AxiosError & { code?: string }).code === 'ERR_CANCELED';
      if (!canceled && status !== 401 && !shouldSuppressToast(finalError.config, message)) {
        reportAppError({
          message,
          code: statusCode,
          title: getErrorTitle(finalError.config),
        });
      }
      return Promise.reject(finalError);
    });
  },
);

export default api;
